import * as React from "react"
import { Input } from "@/components/ui/input"

export interface PriceInputProps extends Omit<React.ComponentProps<"input">, "value" | "onChange" | "type"> {
  value: number
  onValueChange: (value: number) => void
}

const format = (n: number) => n.toFixed(2).replace(".", ",")

/**
 * Money input for Bolivian use:
 * - focusing an empty-valued field (0) clears it, so typing "200" doesn't give "0200"
 * - "." is turned into "," (decimal separator), at most 2 decimals
 * - shows "200,00" when not editing
 */
export const PriceInput = React.forwardRef<HTMLInputElement, PriceInputProps>(
  ({ value, onValueChange, onFocus, onBlur, ...props }, ref) => {
    const [text, setText] = React.useState(() => format(value))
    const [focused, setFocused] = React.useState(false)

    // Follow external changes (e.g. form reset / loaded event) when not editing
    React.useEffect(() => {
      if (!focused) setText(format(value))
    }, [value, focused])

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder={props.placeholder ?? "0,00"}
        value={text}
        onFocus={(e) => {
          setFocused(true)
          if (!value) setText("")
          else e.target.select()
          onFocus?.(e)
        }}
        onChange={(e) => {
          let next = e.target.value.replace(/\./g, ",").replace(/[^0-9,]/g, "")
          const firstComma = next.indexOf(",")
          if (firstComma !== -1) {
            // keep only the first comma, max 2 decimals
            next = next.slice(0, firstComma + 1) + next.slice(firstComma + 1).replace(/,/g, "").slice(0, 2)
          }
          // no leading zeros ("0200" → "200"), but keep "0," for decimals
          next = next.replace(/^0+(?=\d)/, "")
          setText(next)
          const parsed = parseFloat(next.replace(",", "."))
          onValueChange(Number.isNaN(parsed) ? 0 : parsed)
        }}
        onBlur={(e) => {
          setFocused(false)
          const parsed = parseFloat(text.replace(",", "."))
          const final = Number.isNaN(parsed) ? 0 : parsed
          setText(format(final))
          onValueChange(final)
          onBlur?.(e)
        }}
      />
    )
  }
)
PriceInput.displayName = "PriceInput"
